// src/components/ui/HelpFab.js
// Bottom-right FAB — the single entry point into HelpMenuPanel's control center (D-09).
//
// D-09: this was a two-level speed dial (Help, Report a bug) that opened a third
// level, HelpMenuPanel. The split carried no meaning — level 1 held two items and
// level 2 held everything else, so a reader could not predict where anything lived.
// "Report a bug" moved into the drawer's "Give feedback" section, so this file's
// only job now is opening and closing that one surface. A speed dial degrades fast
// past roughly four items (an arc of icon buttons in a corner with no room for
// descriptions, badges, or grouping, and it collapses on outside click so it cannot
// hold anything interactive), while the drawer already carries genuinely dense rows.
//
// D-11: `?` stays the key and this button's icon stays the question mark — it does
// not swap to an X on open, because the drawer's own header close button (from
// useDialogShell) is the close affordance. The keydown listener that fires the `?`
// shortcut lives in the hook imported below; see its own header for the D-12
// text-entry guard.
//
// z-index hierarchy (unchanged values):
//   ToastStack      z-[10000] (top layer: feedback is never hidden by a dialog)
//   Modal           z-[9990]  (centered dialogs)
//   HelpMenuPanel   z-[9985]  (right slide-over drawer)
//   KeyFlowAiDock   z-[9985]  (desktop docked chat; same layer as HelpMenuPanel,
//                              the two are never open together)
//   HelpFab         z-[9980]  (this component)
//   NudgePopover    z-[9975]  (tour auto-prompt, appears just above the FAB)
//
// JIT TRAP: all dynamic dimension values go via inline style, never template-literal
//           Tailwind arbitrary classes.

import React from 'react';
import { Sparkles } from 'lucide-react';
import useHelpShortcut from '../../hooks/useHelpShortcut';

// ---- Icons ----

function QuestionMarkIcon() {
    return (
        <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
        >
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    );
}

// ---- HelpFab ----

// mode (Phase 70, D-01): 'help' is the plain question-mark button. 'ai' swaps the
// icon to Sparkles and names KeyFlow AI; useKeyFlowAiAccess() returns 'ai' only
// when the switch is visible, so a Hidden switch leaves the launcher untouched.
export default function HelpFab({ panelOpen, onOpenPanel, onClosePanel, hasUnseen = false, mode = 'help', rightOffset = 0 }) {
    const isAi = mode === 'ai';
    // D-27: dot while an announcement is unseen and the drawer is closed.
    const showDot = hasUnseen && !panelOpen;
    // D-10: pressing `?` at an already-open drawer is a no-op, not a toggle — a
    // shortcut named "open help" should not close it. Focus placement needs no
    // code here: plan 45-10 wired initialFocusRef into HelpMenuPanel's
    // useDialogShell call, so every open lands the caret in the search field
    // regardless of whether it was triggered by this key or by the click below.
    //
    // D-11 (47-p2-sweep): before opening the drawer, check for a currently
    // rendered tour nudge and, if one exists, focus it instead. A DOM query
    // is deliberate here, not a second useTourNudge() call — AppNudge renders
    // near the end of the App tree, outside this component's subtree, with
    // no shared state between them, so a re-derived `visible` value would be
    // a guess about what is on screen rather than an observation of it.
    // Gated on `!panelOpen` too: a nudge is not reachable behind an open
    // drawer, so stealing focus out to it there would be worse than today's
    // behavior.
    const handleShortcut = React.useCallback(() => {
        if (!panelOpen) {
            const start = document.querySelector('[data-tour="tour-nudge-start"]');
            if (start && start.isConnected) {
                start.focus();
                return;
            }
        }
        if (!panelOpen) onOpenPanel();
    }, [panelOpen, onOpenPanel]);
    useHelpShortcut(handleShortcut);

    const handleClick = React.useCallback(() => {
        if (panelOpen) onClosePanel();
        else onOpenPanel();
    }, [panelOpen, onClosePanel, onOpenPanel]);

    return (
        <div
            className="fixed z-[9980]"
            style={{ bottom: 20, right: 20 + rightOffset, width: 44, height: 44 }}
        >
            <button
                type="button"
                aria-label={panelOpen
                    ? 'Close Help'
                    : isAi
                        ? (showDot ? "Help and KeyFlow AI, new updates in What's New" : 'Help and KeyFlow AI')
                        : showDot ? "Open Help, new updates in What's New" : 'Open Help'}
                title={isAi ? 'Help and KeyFlow AI' : undefined}
                data-tour="help-button"
                onClick={handleClick}
                className="flex items-center justify-center rounded-full
                           bg-[color:var(--color-accent)] text-white
                           hover:opacity-90
                           focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent)]/50
                           transition-[opacity,filter] duration-150"
                style={{
                    width: 44,
                    height: 44,
                    boxShadow: 'var(--shadow-lift)',
                    // Slightly darken the accent while the drawer is open.
                    ...(panelOpen ? { filter: 'brightness(0.92)' } : {}),
                }}
            >
                {isAi ? <Sparkles size={20} aria-hidden="true" focusable="false" /> : <QuestionMarkIcon />}
            </button>
            {showDot && (
                <span
                    data-help-unseen-dot
                    aria-hidden="true"
                    className="rounded-full"
                    style={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 10,
                        height: 10,
                        background: 'var(--color-primary)',
                        border: '2px solid var(--surface-primary)',
                        boxSizing: 'content-box',
                        pointerEvents: 'none',
                    }}
                />
            )}
        </div>
    );
}
